import { useState, useRef, useEffect, useCallback } from "react";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format, isValid, parseISO } from "date-fns";
import TextFieldComponent from "./TextFieldComponent";

const DatePickerComponent = ({
  value: selected,
  name,
  label,
  onChange,
  error,
  sx,
  disabled,
  maxDate,
  isMandatory = false,
  selectTodayOnFocus = false,
  openOnFocus = true, // enable auto-open
}) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const suppressFocusRef = useRef(false); // prevents immediate re-open after close

  const parsedDate = selected ? parseISO(selected) : null;

  const handleFocus = useCallback(() => {
    if (suppressFocusRef.current) return; // don't re-open during suppression window

    if (selectTodayOnFocus && !selected) {
      const today = new Date();
      const formattedToday = format(today, "yyyy-MM-dd");
      onChange({ target: { name, value: formattedToday } });
    }

    if (openOnFocus) setOpen(true);
  }, [openOnFocus, selectTodayOnFocus, selected, onChange, name]);

  const handleChange = (newValue) => {
    if (newValue && isValid(newValue)) {
      const formatted = format(newValue, "yyyy-MM-dd");
      onChange({ target: { name, value: formatted } });
    } else {
      onChange({ target: { name, value: "" } });
    }
    // close and suppress immediate re-open for a short time
    setOpen(false);
    suppressFocusRef.current = true;
    window.setTimeout(() => {
      suppressFocusRef.current = false;
    }, 100); // 100ms is enough to avoid bouncing; tweak if needed
  };

  useEffect(() => {
    // pointerdown covers mouse/touch/pen; falls back to mousedown if pointer isn't supported
    const evtName = window.PointerEvent ? "pointerdown" : "mousedown";

    const onGlobalPointer = (e) => {
      const target = e.target;

      // inside the wrapper (input area) -> do nothing
      if (wrapperRef.current && wrapperRef.current.contains(target)) {
        return;
      }

      // If click is inside MUI picker's popper/calendar area, do nothing (allow selection)
      // We check for typical MUI class-name fragments. This is intentionally fuzzy/tolerant.
      const inPickerPopper =
        target.closest?.('[class*="MuiPickers"]') ||
        target.closest?.('[class*="MuiDatePicker"]') ||
        target.closest?.('[class*="MuiCalendar"]') ||
        target.closest?.('[class*="MuiPickersPopper"]');

      if (inPickerPopper) {
        return;
      }

      // Otherwise it's outside -> close and suppress immediate re-open
      if (open) {
        setOpen(false);
        suppressFocusRef.current = true;
        window.setTimeout(() => {
          suppressFocusRef.current = false;
        }, 100);
      }
    };

    document.addEventListener(evtName, onGlobalPointer, true);
    return () => document.removeEventListener(evtName, onGlobalPointer, true);
  }, [open]);

  return (
    <div ref={wrapperRef}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <DatePicker
          label={
            <span>
              {label}
              {isMandatory && <span className="text-error"> *</span>}
            </span>
          }
          value={parsedDate}
          disabled={disabled}
          maxDate={maxDate}
          open={open}
          onOpen={() => setOpen(true)}   // still allow MUI to signal open
          onClose={() => {
            setOpen(false);
            // suppress immediate re-open triggered by focus/click events
            suppressFocusRef.current = true;
            window.setTimeout(() => {
              suppressFocusRef.current = false;
            }, 100);
          }}
          onChange={handleChange}
          format="dd.MM.yyyy"
          slotProps={{
            textField: {
              error: !!error,
              helperText: error,
              fullWidth: true,
              onFocus: handleFocus, // triggers openOnFocus
            },
          }}
          sx={{
            width: "100%",
            "& .MuiFormLabel-root": {
              position: "static",
              color: "#44475a !important",
            },
            "& .MuiInputBase-input": {
              borderTop: "1px solid #eaeadc",
            },
            "& .MuiButtonBase-root": {
              margin: "0",
            },
            ...sx,
          }}
          renderInput={(params) => <TextFieldComponent {...params} fullWidth />}
        />
      </LocalizationProvider>
    </div>
  );
};

export default DatePickerComponent;
